/**
 * Kill-Cam rolling buffer (invariant #1 safe: transforms only, everything replayed is INTERPOLATED).
 *
 * A short ring of INTERPOLATED transforms (the local hero ship + every remote ship + every cannonball) plus the
 * combat EVENTS (fire / hit / sink / ram) and the ENVIRONMENT (night, storm, wind) of the moment — exactly what
 * the scene director needs to compose a varied cut. It NEVER touches netcode or the sim: main.ts hands it the
 * same interpolated state it feeds the renderer each frame, so the "recording" is a pure by-product of rendering.
 *
 * Design: a fixed ring of pre-allocated frame slots whose ship/ball arrays GROW ONCE to their cap and are then
 * reused — so recording allocates nothing in steady state (the "zero alloc hot-path" rule). Memory is hard-capped
 * by the ring size × per-frame ship/ball caps (REPLAY.*). `freeze()` (a one-shot on the trigger, not the hot path)
 * copies the window out into a compact immutable structure the director + player consume.
 *
 * PURE of DOM / THREE / renderer — unit-tested in replaybuffer.test.ts.
 */
import { REPLAY } from '@piratas/protocol';

/** A ship transform as held in the buffer (superset of net's InterpShip — includes the local hero). */
export interface RepShip { id: number; x: number; y: number; heading: number; speed: number; sail: number; hp: number; flags: number; cls: number; }
/** A cannonball transform (matches net's InterpBall). */
export interface RepBall { id: number; x: number; y: number; heading: number; elev: number; }
/** A captured combat event. `kind` is the protocol EventType; the a/b/c payload is kind-specific:
 *   FIRE → {x,y = firing ship pos, a = shipId, b = side, c = ammo}
 *   HIT  → {x,y = impact,        a = byId,   b = victimId, c = rake(0/1)}
 *   SINK → {x,y = sink pos,      a = victimId, b = killerId, c = FATAL BALL ID (v56; 0 = ram/flood)}
 *   RAM  → {x,y = impact,        a = mag(0..255)}
 * The SINK's `c` (fatal ball id) is the linchpin of the narrative director: it names the EXACT cannonball whose
 * launch→impact trajectory the director rides as the hero — no longer a "closest ball to the impact point" guess. */
export interface RepEvent { kind: number; t: number; x: number; y: number; a: number; b: number; c: number; }

export interface RepEnv { night: number; storm: number; windDir: number; windStrength: number; }

/** A ship/ball transform source the recorder accepts (net's InterpShip/InterpBall satisfy these structurally). */
export interface ShipLike { id: number; x: number; y: number; heading: number; speed: number; sail: number; hp: number; flags: number; cls: number; }
export interface BallLike { id: number; x: number; y: number; heading: number; elev: number; }

interface Frame {
  used: boolean;
  t: number;
  env: RepEnv;
  hasSelf: boolean;
  self: RepShip;
  ships: RepShip[]; // reusable pool; shipN valid entries
  shipN: number;
  balls: RepBall[]; // reusable pool; ballN valid entries
  ballN: number;
}

function blankShip(): RepShip { return { id: 0, x: 0, y: 0, heading: 0, speed: 0, sail: 0, hp: 0, flags: 0, cls: 0 }; }
function blankBall(): RepBall { return { id: 0, x: 0, y: 0, heading: 0, elev: 0 }; }
function copyShip(dst: RepShip, s: ShipLike): void {
  dst.id = s.id; dst.x = s.x; dst.y = s.y; dst.heading = s.heading; dst.speed = s.speed; dst.sail = s.sail; dst.hp = s.hp; dst.flags = s.flags; dst.cls = s.cls;
}

/** The immutable window handed to the director + player on trigger. */
export interface FrozenReplay {
  frames: FrozenFrame[]; // oldest → newest, monotonically increasing t
  events: RepEvent[];    // within [t0, t1], chronological
  t0: number;            // first frame sim time
  t1: number;            // last frame sim time
  killT: number;         // sim time of the fatal event (the trigger)
}
export interface FrozenFrame { t: number; env: RepEnv; self: RepShip | null; ships: RepShip[]; balls: RepBall[]; }

export class ReplayBuffer {
  private ring: Frame[] = [];
  private head = -1;      // index of the most recent frame, or -1
  private count = 0;      // frames written (≤ capacity)
  private lastCaptureT = -1e9;
  private events: RepEvent[] = [];
  private evHead = -1;
  private evCount = 0;
  readonly capacity: number;
  private readonly captureDt: number;

  constructor() {
    // +2 slots of slack so a full WINDOW+TAIL is always available even at a slightly irregular capture cadence.
    this.capacity = Math.ceil((REPLAY.WINDOW_S + REPLAY.TAIL_S) * REPLAY.CAPTURE_HZ) + 2;
    this.captureDt = 1 / REPLAY.CAPTURE_HZ;
    for (let i = 0; i < this.capacity; i++) {
      this.ring.push({ used: false, t: 0, env: { night: 0, storm: 0, windDir: 0, windStrength: 1 }, hasSelf: false, self: blankShip(), ships: [], shipN: 0, balls: [], ballN: 0 });
    }
    for (let i = 0; i < REPLAY.MAX_EVENTS; i++) this.events.push({ kind: 0, t: 0, x: 0, y: 0, a: 0, b: 0, c: 0 });
  }

  /** True once there's enough tape to make a meaningful clip (≈ half the window). */
  ready(): boolean { return this.count >= Math.floor(REPLAY.WINDOW_S * REPLAY.CAPTURE_HZ * 0.5); }

  /** Sim time of the most recent recorded frame, or -1 if empty (a spontaneous manual capture anchors here). */
  latestT(): number { return this.head >= 0 && this.ring[this.head]!.used ? this.ring[this.head]!.t : -1; }

  /** Record one frame of interpolated state. Rate-limited to CAPTURE_HZ (extra rAF calls are dropped, free).
   *  `self` is the local hero (null while dead/unspawned). ZERO allocation in steady state. */
  recordFrame(t: number, self: ShipLike | null, ships: readonly ShipLike[], balls: readonly BallLike[], env: RepEnv): void {
    if (t < this.lastCaptureT + this.captureDt) return; // throttle to the capture cadence
    this.lastCaptureT = t;
    this.head = (this.head + 1) % this.capacity;
    if (this.count < this.capacity) this.count++;
    const f = this.ring[this.head]!;
    f.used = true; f.t = t;
    f.env.night = env.night; f.env.storm = env.storm; f.env.windDir = env.windDir; f.env.windStrength = env.windStrength;
    f.hasSelf = !!self;
    if (self) copyShip(f.self, self);
    // ships (cap; keep the first MAX_SHIPS — callers pass AOI-limited sets so order ≈ relevance)
    const sn = Math.min(ships.length, REPLAY.MAX_SHIPS);
    while (f.ships.length < sn) f.ships.push(blankShip());
    for (let i = 0; i < sn; i++) copyShip(f.ships[i]!, ships[i]!);
    f.shipN = sn;
    // balls (cap)
    const bn = Math.min(balls.length, REPLAY.MAX_BALLS);
    while (f.balls.length < bn) f.balls.push(blankBall());
    for (let i = 0; i < bn; i++) { const d = f.balls[i]!, s = balls[i]!; d.id = s.id; d.x = s.x; d.y = s.y; d.heading = s.heading; d.elev = s.elev; }
    f.ballN = bn;
  }

  /** Record a combat event into the capped rolling event ring (called from the FX drain in main.ts). */
  recordEvent(kind: number, t: number, x: number, y: number, a = 0, b = 0, c = 0): void {
    this.evHead = (this.evHead + 1) % REPLAY.MAX_EVENTS;
    if (this.evCount < REPLAY.MAX_EVENTS) this.evCount++;
    const e = this.events[this.evHead]!;
    e.kind = kind; e.t = t; e.x = x; e.y = y; e.a = a; e.b = b; e.c = c;
  }

  /** Copy the [killT − WINDOW, killT + TAIL] window out into an immutable structure. One-shot (not hot-path). */
  freeze(killT: number): FrozenReplay {
    const t0 = killT - REPLAY.WINDOW_S;
    const t1 = killT + REPLAY.TAIL_S;
    const frames: FrozenFrame[] = [];
    // walk the ring oldest → newest
    const start = this.count < this.capacity ? 0 : (this.head + 1) % this.capacity;
    for (let i = 0; i < this.count; i++) {
      const f = this.ring[(start + i) % this.capacity]!;
      if (!f.used || f.t < t0 || f.t > t1) continue;
      frames.push({
        t: f.t,
        env: { ...f.env },
        self: f.hasSelf ? { ...f.self } : null,
        ships: f.ships.slice(0, f.shipN).map((s) => ({ ...s })),
        balls: f.balls.slice(0, f.ballN).map((b) => ({ ...b })),
      });
    }
    frames.sort((p, q) => p.t - q.t);
    const events: RepEvent[] = [];
    const es = this.evCount < REPLAY.MAX_EVENTS ? 0 : (this.evHead + 1) % REPLAY.MAX_EVENTS;
    for (let i = 0; i < this.evCount; i++) {
      const e = this.events[(es + i) % REPLAY.MAX_EVENTS]!;
      if (e.t >= t0 && e.t <= t1) events.push({ ...e });
    }
    events.sort((p, q) => p.t - q.t);
    const realT0 = frames.length ? frames[0]!.t : t0;
    const realT1 = frames.length ? frames[frames.length - 1]!.t : t1;
    return { frames, events, t0: realT0, t1: realT1, killT };
  }

  /** Wipe the tape (e.g. on death/respawn baseline resets, port transitions). */
  clear(): void {
    this.head = -1; this.count = 0; this.evHead = -1; this.evCount = 0; this.lastCaptureT = -1e9;
    for (const f of this.ring) f.used = false;
  }
}

/* ------------------------------------- pure sampling helpers ------------------------------------- */

function lerp(a: number, b: number, k: number): number { return a + (b - a) * k; }
function lerpAngle(a: number, b: number, k: number): number {
  let d = b - a; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2; return a + d * k;
}

/** Find the two frames bracketing sim time `t` and the interpolation alpha between them. Clamps at the ends. */
export function bracketFrames(frozen: FrozenReplay, t: number): { a: FrozenFrame; b: FrozenFrame; alpha: number } | null {
  const fs = frozen.frames; const n = fs.length;
  if (n === 0) return null;
  if (n === 1) return { a: fs[0]!, b: fs[0]!, alpha: 0 };
  if (t <= fs[0]!.t) return { a: fs[0]!, b: fs[0]!, alpha: 0 };
  if (t >= fs[n - 1]!.t) return { a: fs[n - 1]!, b: fs[n - 1]!, alpha: 0 };
  for (let i = n - 1; i > 0; i--) {
    const a = fs[i - 1]!, b = fs[i]!;
    if (a.t <= t && t <= b.t) { const span = b.t - a.t; return { a, b, alpha: span > 0 ? (t - a.t) / span : 0 }; }
  }
  return { a: fs[0]!, b: fs[0]!, alpha: 0 };
}

/** Interpolated position of one ship id at sim time `t`, or null if absent at that moment. Reuses `out`. */
export function shipAt(frozen: FrozenReplay, id: number, t: number, out: RepShip): RepShip | null {
  const br = bracketFrames(frozen, t); if (!br) return null;
  const findIn = (fr: FrozenFrame): RepShip | null => {
    if (id < 0 && fr.self && fr.self.id === id) return fr.self;
    if (fr.self && fr.self.id === id) return fr.self;
    for (const s of fr.ships) if (s.id === id) return s;
    return null;
  };
  const sa = findIn(br.a); const sb = findIn(br.b);
  if (!sa && !sb) return null;
  const A = sa ?? sb!; const B = sb ?? sa!;
  out.id = id;
  out.x = lerp(A.x, B.x, br.alpha); out.y = lerp(A.y, B.y, br.alpha);
  out.heading = lerpAngle(A.heading, B.heading, br.alpha);
  out.speed = lerp(A.speed, B.speed, br.alpha); out.sail = lerp(A.sail, B.sail, br.alpha);
  out.hp = lerp(A.hp, B.hp, br.alpha); out.flags = B.flags; out.cls = A.cls;
  return out;
}

/** Interpolated position of one cannonball id at sim time `t`, or null if it isn't in flight then. Reuses `out`. */
export function ballAt(frozen: FrozenReplay, id: number, t: number, out: RepBall): RepBall | null {
  const br = bracketFrames(frozen, t); if (!br) return null;
  const findIn = (fr: FrozenFrame): RepBall | null => { for (const b of fr.balls) if (b.id === id) return b; return null; };
  const ba = findIn(br.a); const bb = findIn(br.b);
  if (!ba && !bb) return null;
  const A = ba ?? bb!; const B = bb ?? ba!;
  out.id = id;
  out.x = lerp(A.x, B.x, br.alpha); out.y = lerp(A.y, B.y, br.alpha);
  out.heading = lerpAngle(A.heading, B.heading, br.alpha); out.elev = lerp(A.elev, B.elev, br.alpha);
  return out;
}

/** Sample the WHOLE scene at sim time `t` into reused output arrays (self + ships + balls + env). No per-call
 *  allocation once the pools have grown. Used by the player to feed renderer.syncShips / syncBalls each frame. */
export interface SamplePools { self: RepShip; ships: RepShip[]; balls: RepBall[]; env: RepEnv; shipN: number; ballN: number; hasSelf: boolean; }
export function makeSamplePools(): SamplePools {
  const ships: RepShip[] = []; for (let i = 0; i < REPLAY.MAX_SHIPS; i++) ships.push(blankShip());
  const balls: RepBall[] = []; for (let i = 0; i < REPLAY.MAX_BALLS; i++) balls.push(blankBall());
  return { self: blankShip(), ships, balls, env: { night: 0, storm: 0, windDir: 0, windStrength: 1 }, shipN: 0, ballN: 0, hasSelf: false };
}
export function sampleScene(frozen: FrozenReplay, t: number, out: SamplePools): void {
  const br = bracketFrames(frozen, t);
  out.shipN = 0; out.ballN = 0; out.hasSelf = false;
  if (!br) return;
  const { a, b, alpha } = br;
  // env from the nearer frame
  const env = alpha < 0.5 ? a.env : b.env;
  out.env.night = env.night; out.env.storm = env.storm; out.env.windDir = env.windDir; out.env.windStrength = env.windStrength;
  // self
  if (a.self) {
    out.hasSelf = true;
    const bs = b.self && b.self.id === a.self.id ? b.self : a.self;
    const d = out.self;
    d.id = a.self.id; d.x = lerp(a.self.x, bs.x, alpha); d.y = lerp(a.self.y, bs.y, alpha);
    d.heading = lerpAngle(a.self.heading, bs.heading, alpha); d.speed = lerp(a.self.speed, bs.speed, alpha);
    d.sail = lerp(a.self.sail, bs.sail, alpha); d.hp = lerp(a.self.hp, bs.hp, alpha); d.flags = bs.flags; d.cls = a.self.cls;
  }
  // ships: interpolate each of frame a's ships toward its match in frame b
  for (const sa of a.ships) {
    if (out.shipN >= REPLAY.MAX_SHIPS) break;
    let sb: RepShip | null = null; for (const c of b.ships) if (c.id === sa.id) { sb = c; break; }
    const d = out.ships[out.shipN]!;
    d.id = sa.id; d.cls = sa.cls;
    if (sb) { d.x = lerp(sa.x, sb.x, alpha); d.y = lerp(sa.y, sb.y, alpha); d.heading = lerpAngle(sa.heading, sb.heading, alpha); d.speed = lerp(sa.speed, sb.speed, alpha); d.sail = lerp(sa.sail, sb.sail, alpha); d.hp = sb.hp; d.flags = sb.flags; }
    else { d.x = sa.x; d.y = sa.y; d.heading = sa.heading; d.speed = sa.speed; d.sail = sa.sail; d.hp = sa.hp; d.flags = sa.flags; }
    out.shipN++;
  }
  // balls
  for (const ba of a.balls) {
    if (out.ballN >= REPLAY.MAX_BALLS) break;
    let bb: RepBall | null = null; for (const c of b.balls) if (c.id === ba.id) { bb = c; break; }
    const d = out.balls[out.ballN]!;
    d.id = ba.id;
    if (bb) { d.x = lerp(ba.x, bb.x, alpha); d.y = lerp(ba.y, bb.y, alpha); d.heading = lerpAngle(ba.heading, bb.heading, alpha); d.elev = lerp(ba.elev, bb.elev, alpha); }
    else { d.x = ba.x; d.y = ba.y; d.heading = ba.heading; d.elev = ba.elev; }
    out.ballN++;
  }
}
